import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as HtmlDiffModule from 'htmldiff-js';
const HtmlDiff = HtmlDiffModule.default;

/**
 * Diff Viewer Component
 *
 * A reusable component for displaying HTML diffs with track changes visualization.
 * Uses htmldiff-js for semantic HTML diffing and normalizes HTML to avoid
 * false positives from whitespace/formatting differences.
 *
 * Features:
 * - Semantic HTML diff (not just text diff)
 * - HTML normalization to reduce false positives
 * - Green highlighting for additions
 * - Red highlighting with strikethrough for deletions
 * - Optional legend display
 */
@Component({
  standalone: false,
  selector: 'izzy-diff-viewer',
  templateUrl: './diff-viewer.component.html',
  styleUrls: ['./diff-viewer.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class DiffViewerComponent implements OnInit, OnChanges {

  /** The original/base content */
  @Input() originalContent: string = '';

  /** The modified/new content */
  @Input() modifiedContent: string = '';

  /** Whether to show the legend header */
  @Input() showLegend: boolean = true;

  /** Optional title for the diff view */
  @Input() title: string = 'Track Changes';

  /** Computed HTML diff for display */
  diffHtml: SafeHtml = '';

  /** Whether there are any differences */
  hasDifferences: boolean = false;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.computeDiff();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['originalContent'] || changes['modifiedContent']) {
      this.computeDiff();
    }
  }

  /**
   * Compute the HTML diff between original and modified content
   */
  private computeDiff(): void {
    const original = this.normalizeHtml(this.originalContent || '');
    const modified = this.normalizeHtml(this.modifiedContent || '');

    if (!original && !modified) {
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml('');
      this.hasDifferences = false;
      return;
    }

    // Check if there are actual differences
    this.hasDifferences = original !== modified;

    if (!this.hasDifferences) {
      // No differences - just show the content as-is
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml(modified || original);
      return;
    }

    try {
      // Use htmldiff-js to compute semantic HTML diff
      const diff = HtmlDiff.execute(original, modified);
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml(diff);
    } catch (error) {
      console.error('Error computing HTML diff:', error);
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml(modified || original);
    }
  }

  /**
   * Normalize HTML to reduce false positives in diff
   * - Standardize whitespace
   * - Remove empty attributes
   * - Normalize self-closing tags
   */
  private normalizeHtml(html: string): string {
    if (!html) return '';

    return html
      // Normalize multiple spaces/newlines to single space
      .replace(/\s+/g, ' ')
      // Remove spaces around tags
      .replace(/>\s+</g, '><')
      // Remove leading/trailing whitespace
      .trim()
      // Normalize &nbsp; to regular spaces for comparison
      .replace(/&nbsp;/g, ' ')
      // Normalize multiple spaces again after &nbsp; replacement
      .replace(/\s+/g, ' ')
      // Remove empty style attributes
      .replace(/\s*style=""\s*/g, ' ')
      // Remove empty class attributes
      .replace(/\s*class=""\s*/g, ' ')
      // Normalize self-closing tags
      .replace(/<br\s*\/?>/gi, '<br>')
      .replace(/<hr\s*\/?>/gi, '<hr>')
      // Final trim
      .trim();
  }
}
